import { supabase } from "@/integrations/supabase/client";
import { CommunicationWithRelations } from "@/types/communication.types";

export type Communication = {
  id?: string;
  type: string;
  date: Date | string;
  subject: string;
  content?: string;
  organization_id?: string;
  contact_id?: string;
  opportunity_id?: string;
  status?: string;
  follow_up_date?: Date | string | null;
  follow_up_type?: string;
  sentiment?: string;
  response_required?: boolean;
  is_outbound?: boolean;
  tags?: string[];
  custom_fields?: Record<string, any>;
  user_id?: string;
};

/**
 * Fetch all communications with related entities
 */
export const fetchCommunications = async (filters?: {
  organization_id?: string;
  contact_id?: string;
  opportunity_id?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  types?: string[];
}) => {
  let query = supabase
    .from("communications")
    .select(`
      *,
      organization:organizations(*),
      contact:contacts(*),
      opportunity:opportunities(*)
    `)
    .order("date", { ascending: false });

  // Apply filters if provided
  if (filters) {
    if (filters.organization_id) {
      query = query.eq("organization_id", filters.organization_id);
    }
    if (filters.contact_id) {
      query = query.eq("contact_id", filters.contact_id);
    }
    if (filters.opportunity_id) {
      query = query.eq("opportunity_id", filters.opportunity_id);
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }
    if (filters.types && filters.types.length > 0) {
      query = query.in("type", filters.types);
    }
    if (filters.startDate && filters.endDate) {
      query = query
        .gte("date", filters.startDate)
        .lte("date", filters.endDate);
    }
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
};

/**
 * Fetch a single communication by ID
 */
export const fetchCommunicationById = async (id: string): Promise<CommunicationWithRelations> => {
  const { data, error } = await supabase
    .from("communications")
    .select(`
      *,
      organization:organizations(*),
      contact:contacts(*),
      opportunity:opportunities(*)
    `)
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
};

/**
 * Create a new communication
 */
export const createCommunication = async (communication: Communication) => {
  const { data, error } = await supabase
    .from("communications")
    .insert({
      type: communication.type,
      date: new Date(communication.date).toISOString(),
      subject: communication.subject,
      content: communication.content,
      organization_id: communication.organization_id || null,
      contact_id: communication.contact_id || null,
      opportunity_id: communication.opportunity_id || null,
      status: communication.status || "completed",
      follow_up_date: communication.follow_up_date 
        ? new Date(communication.follow_up_date).toISOString() 
        : null,
      follow_up_type: communication.follow_up_type || null,
      sentiment: communication.sentiment || "neutral",
      response_required: communication.response_required || false,
      is_outbound: communication.is_outbound || true,
      tags: communication.tags || [],
      custom_fields: communication.custom_fields || {},
      user_id: communication.user_id
    })
    .select()
    .single();

  if (error) throw error;
  
  // If there's a contact, update the last contacted date
  if (communication.contact_id) {
    await supabase
      .from("contacts")
      .update({ last_contacted_date: new Date().toISOString() })
      .eq("id", communication.contact_id);
  }
  
  return data;
};

/**
 * Update an existing communication
 */
export const updateCommunication = async (id: string, updates: Partial<Communication>) => {
  const updateData: any = { ...updates };
  
  // Convert dates to ISO strings
  if (updates.date) {
    updateData.date = new Date(updates.date).toISOString();
  }
  
  if (updates.follow_up_date) {
    updateData.follow_up_date = new Date(updates.follow_up_date).toISOString();
  } else if (updates.follow_up_date === null) {
    updateData.follow_up_date = null;
  }

  const { data, error } = await supabase
    .from("communications")
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete a communication
 */
export const deleteCommunication = async (id: string) => {
  const { error } = await supabase
    .from("communications")
    .delete()
    .eq("id", id);

  if (error) throw error;
  return true;
};

/**
 * Create a follow-up communication based on an existing one
 */
export const createFollowUpCommunication = async (
  originalId: string,
  followUpData: Partial<Communication>
) => {
  // First, get the original communication
  const original = await fetchCommunicationById(originalId);
  
  // Create a new communication with some data from the original
  const newComm: Communication = {
    type: followUpData.type || original.type,
    date: followUpData.date || new Date(),
    subject: followUpData.subject || `Follow-up: ${original.subject}`,
    content: followUpData.content || "",
    organization_id: original.organization_id,
    contact_id: original.contact_id,
    opportunity_id: original.opportunity_id,
    status: followUpData.status || "completed",
    tags: followUpData.tags || original.tags || [],
    is_outbound: followUpData.is_outbound || true,
    user_id: followUpData.user_id
  };
  
  // Create the follow-up communication
  const result = await createCommunication(newComm);
  
  // Update the original communication to indicate it has been followed up
  await updateCommunication(originalId, {
    status: "completed"
  });
  
  return result;
};

/**
 * Fetch tags for communications
 */
export const fetchCommunicationTags = async () => {
  // In a real app, this might fetch from a tags table
  // For now, we'll return a predefined list
  return [
    "Important",
    "Follow-up",
    "Marketing",
    "Sales",
    "Support",
    "Technical",
    "Billing",
    "Contract",
    "Partnership",
  ];
};

/**
 * Get communication stats by type and period
 */
export const getCommunicationStats = async (
  period: 'week' | 'month' | 'quarter' | 'year' = 'month',
  organizationId?: string
) => {
  let startDate;
  const now = new Date();
  
  // Calculate the start date based on the period
  switch (period) {
    case 'week':
      startDate = new Date(now.setDate(now.getDate() - 7));
      break;
    case 'month':
      startDate = new Date(now.setMonth(now.getMonth() - 1));
      break;
    case 'quarter':
      startDate = new Date(now.setMonth(now.getMonth() - 3));
      break;
    case 'year':
      startDate = new Date(now.setFullYear(now.getFullYear() - 1));
      break;
  }
  
  // Fetch communications for the period
  let query = supabase
    .from("communications")
    .select(`
      type, 
      date,
      organization_id,
      contact_id
    `)
    .gte("date", startDate.toISOString());
    
  if (organizationId) {
    query = query.eq("organization_id", organizationId);
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  // Process the data to get stats
  const stats = {
    total: data.length,
    byType: {} as Record<string, number>,
    trend: [] as any[],
  };
  
  // Count by type
  data.forEach(comm => {
    const type = comm.type;
    if (!stats.byType[type]) {
      stats.byType[type] = 0;
    }
    stats.byType[type]++;
  });
  
  return stats;
};